import clsx from 'clsx';
import {
    MATCH_SIGNAL_CHIPS_MAX,
    MATCH_SIGNAL_CHIP_EXCLUDED_TYPES,
    MATCH_SIGNAL_NAME_TYPES,
    MATCH_SIGNAL_TYPE,
    MATCH_SIGNAL_TYPE_LABEL,
    MATCH_STATUS_TEXT,
    matchSignalChipsOverflowLabel,
} from './constants';
import styles from './MatchSignalChips.module.scss';
import type { MatchEvidence, MatchEvidenceSignal, MatchSignalChip, MatchStatus } from './types';

export interface MatchSignalChipsProps {
    /** Already selected by `selectMatchSignalChips`, so a caller that also needs them derives them once. */
    chips: MatchSignalChip[];
    maxChips?: number;
}

// The engine writes the IDENTIFIER note as "<KIND> matched" for one of these four kinds, plus one
// fixed literal for a PAN recovered from inside a GSTIN. Anchored and exact -- "GSTIN" appears as
// a substring of other notes, so a loose match would mislabel them as a GSTIN hit.
const IDENTIFIER_NOTE_KIND = /^(PAN|DIN|CIN|GSTIN) matched$/;
const IDENTIFIER_PAN_IN_GSTIN_NOTE = 'PAN inside the GSTIN matched';

const identifierNoun = (note: string): string => {
    const kind = IDENTIFIER_NOTE_KIND.exec(note)?.[1];
    if (kind) return kind;
    return note === IDENTIFIER_PAN_IN_GSTIN_NOTE ? 'PAN' : 'Identifier';
};

const signalNoun = (signal: MatchEvidenceSignal): string =>
    signal.type === MATCH_SIGNAL_TYPE.IDENTIFIER ? identifierNoun(signal.note) : MATCH_SIGNAL_TYPE_LABEL[signal.type] ?? signal.label;

const higherScoring = (leader: MatchEvidenceSignal, candidate: MatchEvidenceSignal): MatchEvidenceSignal =>
    (candidate.score ?? -Infinity) > (leader.score ?? -Infinity) ? candidate : leader;

const toChip = (signal: MatchEvidenceSignal, isNameWinner: boolean, reasonsTitle?: string): MatchSignalChip => {
    const status = signal.status as MatchStatus;
    // Percent is a name-chip-only affordance -- the design shows it nowhere else, even when a
    // non-name signal happens to carry a score. typeof, not !== null: wire data can omit the
    // field entirely, and Math.round(undefined) would otherwise print "NaN%".
    const percent = isNameWinner && typeof signal.score === 'number' ? ` ${Math.round(signal.score)}%` : '';
    return {
        key: signal.type,
        status,
        text: `${signalNoun(signal)} ${MATCH_STATUS_TEXT[status].chipVerb}${percent}`,
        title: isNameWinner ? reasonsTitle : undefined,
    };
};

/**
 * `matchEvidence.signals[]`, collapsed to the ordered chip set a regulatory hit's header shows:
 * one chip per signal that was actually compared and got a status (legal form and incorporation
 * date never get one), every name-family signal folded into a single chip for its highest scorer,
 * in the engine's own served order.
 */
export const selectMatchSignalChips = (matchEvidence?: MatchEvidence): MatchSignalChip[] => {
    if (!matchEvidence) return [];
    const { signals, reasons } = matchEvidence;
    const compared = signals.filter(
        (signal) => signal.compared && signal.status !== null && !MATCH_SIGNAL_CHIP_EXCLUDED_TYPES.has(signal.type),
    );
    const nameCandidates = compared.filter((signal) => MATCH_SIGNAL_NAME_TYPES.has(signal.type));
    const nameWinner = nameCandidates.length > 0 ? nameCandidates.reduce(higherScoring) : undefined;
    // reasons[] stays reachable without crowding the header: a native tooltip on the name chip,
    // never a visible list.
    const reasonsTitle = reasons?.length ? reasons.join(' · ') : undefined;

    return compared
        .filter((signal) => !MATCH_SIGNAL_NAME_TYPES.has(signal.type) || signal === nameWinner)
        .map((signal) => toChip(signal, signal === nameWinner, reasonsTitle));
};

// Status modifier classes are named after the status itself (Chip_EXACT, Chip_PARTIAL, Chip_MISMATCH).
const chipStatusClass = (status: MatchStatus) => styles[`Chip_${status}`];

/**
 * Header chips for a regulatory hit's matched signals. Selection, name-collapsing and wording
 * live in `selectMatchSignalChips` -- pure derivation, no drawer knowledge, so any other surface
 * can render the same evidence the same way. This component only lays the result out, capped to
 * `maxChips` with the rest folded into a "+N more" pill, and renders nothing when no chip
 * survived selection, so a caller can safely fall back to its own chip.
 */
const MatchSignalChips = ({ chips, maxChips = MATCH_SIGNAL_CHIPS_MAX }: MatchSignalChipsProps) => {
    if (chips.length === 0) return null;

    const visible = chips.slice(0, maxChips);
    const overflow = chips.slice(maxChips);

    return (
        <span className={styles.Chips}>
            {visible.map((chip) => (
                <span key={chip.key} className={clsx(styles.Chip, chipStatusClass(chip.status))} title={chip.title}>
                    {chip.text}
                </span>
            ))}
            {overflow.length > 0 && (
                <span className={clsx(styles.Chip, styles.Chip_Overflow)} title={overflow.map((chip) => chip.text).join(', ')}>
                    {matchSignalChipsOverflowLabel(overflow.length)}
                </span>
            )}
        </span>
    );
};

export default MatchSignalChips;
